// MACHINERY V2 (Stroje) — connectivity dot (M2.1).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §4.3 (LOCKED).
// Reference impl: design_handoff_machinery_v3/machinery-live.jsx
// (ConnectivityDot).
//
// An 8px signal that is SEPARATE from the status badge: the badge says what
// the machine is doing, the dot says how much we trust that reading.
//   live      → green, gentle pulse
//   stale     → amber (fires together with the badge desaturation — both
//               originate from `conn`, via cardStates.isStale)
//   longstale → faint grey with an X knockout, so a grey dot is never
//               ambiguous against "no signal at all"
//   null      → renders NOTHING. An unconnected machine (99% of the fleet)
//               shows no dot, no badge, nothing. This is the rule that keeps
//               v2 looking like today's grid for everyone without an agent.
//
// The pulse uses the scoped `machinery-v2-pulse` keyframe from
// machinery-v2-tokens.scss (NOT the MIDAS sheet's midas-pulse — v2 must not
// depend on another section's stylesheet being loaded). The sheet's
// prefers-reduced-motion block disables it.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { MachineryV2Conn } from "./cardStates";

export interface ConnDotProps {
  state: MachineryV2Conn | null;
  size?: number;
  /** Render the heartbeat age (or the state label) next to the dot. */
  withLabel?: boolean;
  /** Human heartbeat age, e.g. "pred 4 s" — becomes the hover title. */
  lastHeartbeat?: string | null;
}

interface DotSpec {
  color: string;
  labelKey: string;
  pulse: boolean;
}

const DOT_SPECS: Record<"live" | "stale" | "longstale", DotSpec> = {
  live: { color: "var(--green)", labelKey: "myMachinery:v2.conn.live", pulse: true },
  stale: { color: "var(--amber)", labelKey: "myMachinery:v2.conn.stale", pulse: false },
  longstale: {
    color: "var(--faint)",
    labelKey: "myMachinery:v2.conn.longstale",
    pulse: false,
  },
};

const ConnDot: React.FC<ConnDotProps> = ({
  state,
  size = 8,
  withLabel = false,
  lastHeartbeat = null,
}) => {
  const { translate } = useTranslation("myMachinery");

  // null = unconnected, and "awaiting" has its own badge instead of a dot
  // (there is no heartbeat to grade yet).
  if (!state || state === "awaiting") return null;

  const spec = DOT_SPECS[state];
  const label = translate(spec.labelKey);

  const dot = (
    <span
      title={
        lastHeartbeat
          ? translate("myMachinery:v2.conn.lastHeartbeat") + ": " + lastHeartbeat
          : label
      }
      style={{
        position: "relative",
        display: "inline-block",
        width: size,
        height: size,
        borderRadius: 999,
        background: spec.color,
        flexShrink: 0,
      }}
    >
      {spec.pulse && (
        <span
          style={{
            position: "absolute",
            inset: -3,
            borderRadius: 999,
            background: spec.color,
            opacity: 0.18,
            animation: "machinery-v2-pulse 1.6s infinite ease-in-out",
          }}
        />
      )}
      {state === "longstale" && (
        <svg
          viewBox="0 0 8 8"
          style={{ position: "absolute", inset: 0, color: "var(--bg)" }}
          aria-hidden
        >
          <line
            x1="2.2"
            y1="2.2"
            x2="5.8"
            y2="5.8"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
          <line
            x1="5.8"
            y1="2.2"
            x2="2.2"
            y2="5.8"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
        </svg>
      )}
    </span>
  );

  if (!withLabel) return dot;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontFamily: "var(--font-mono)",
        fontSize: 10.5,
        color: "var(--faint)",
      }}
    >
      {dot}
      <span>{lastHeartbeat || label}</span>
    </span>
  );
};

export default ConnDot;
